import ReactDom from 'react-dom';

import { Icon as BlueIcon } from '@blueprintjs/core';

import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { CustomPicker } from '@components/CustomPickerWithBorder/CustomPicker';
import { createTag } from '@services/queries/tags';
import { indexTagsCategory } from '@services/queries/tagsCategory';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { Controller, useForm } from 'react-hook-form';
import { CategoryColorBadge } from '@components/CategoryColorBadge/CategoryColorBadge';
import { trackAddTag } from '@services/mixpanel/trackers/TagsMetrics';
import { useState } from 'react';
import {
  Close,
  Confirm,
  Container,
  Divider,
  Error,
  Form,
  Header,
  Input,
  Label,
  Line,
  Overlay,
  Textarea,
  Title,
} from './AddTagDialog.styles';
import { DUPLICATE_TAG_ERROR_MESSAGE } from './AddTagDialog.utils';

interface IAddTagDialog {
  open: boolean;
  onClose: () => void;
}

interface IFormData {
  name: string;
  description: string;
  tagCategoryId: string;
}
export const AddTagDialog = ({ open, onClose }: IAddTagDialog) => {
  const portalDiv = document.getElementById('portal');

  const queryClient = useQueryClient();

  const { data } = useQuery(['tagsCategory'], () => indexTagsCategory());

  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const category = data?.find(e => e.id === selectedCategoryId);
  const filteredCategory = category ? category.name : '';

  const Options = data?.map(option => {
    return {
      value: option.id,
      label: <CategoryColorBadge color={option.color} name={option.name} />,
    };
  });
  const schema = yup.object().shape({
    name: yup
      .string()
      .max(24, 'Tag name must be under the 24 character limit')
      .required(),
    description: yup.string(),
    tagCategoryId: yup.string().required(),
  });

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    getValues,
    formState: { isValid, errors },
    control,
  } = useForm<IFormData>({
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  const mutationCreateTag = useMutation(createTag, {
    onSuccess: () => {
      queryClient.invalidateQueries('tags');
      queryClient.invalidateQueries('tagCategories');
      trackAddTag(
        getValues('name'),
        filteredCategory,
        !!getValues('description'),
      );

      onClose();
      setValue('name', '');
      setValue('description', '');
      setValue('tagCategoryId', '');
    },
    onError: (error: any) => {
      if (error.response?.data?.message === DUPLICATE_TAG_ERROR_MESSAGE) {
        setError('name', {
          type: 'validate',
          message: DUPLICATE_TAG_ERROR_MESSAGE,
        });
      }
    },
  });

  const onSubmit = (Tag: IFormData) => {
    if (!mutationCreateTag.isLoading) {
      if (data)
        mutationCreateTag.mutate({
          name: Tag.name,
          description: Tag.description,
          tagCategoryId: Tag.tagCategoryId,
        });
    }
  };
  let showErrMsg = false;
  let isValidNameErr = false;
  if (errors.name) {
    isValidNameErr =
      errors.name.type === 'max' || errors.name.type === 'validate';
    showErrMsg = Boolean(isValidNameErr && errors.name?.message);
  }

  return open && portalDiv
    ? ReactDom.createPortal(
        <Overlay>
          <Container>
            <Header>
              <Title>Add Tag</Title>
              <Close
                onClick={() => {
                  onClose();
                  clearErrors('name');
                }}
              >
                <BlueIcon icon="cross" color="#8F95B2" />
              </Close>
            </Header>
            <Divider height="28px" />
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Label className="mandatory">Tag name</Label>

              <Input
                placeholder="Ex. Studio"
                autoFocus
                {...register('name')}
                isError={isValidNameErr}
              />
              {showErrMsg && <Error>{errors?.name?.message}</Error>}

              <Divider height="16px" />

              <Label className="mandatory">Category</Label>

              <Controller
                control={control}
                name="tagCategoryId"
                render={({ field: { onChange, value } }) => (
                  <CustomPicker
                    options={Options}
                    placeholder="Select a Category"
                    value={Options?.find(c => c.value === value)}
                    onChange={(val: any) => {
                      onChange(val.value);
                      setSelectedCategoryId(val.value);
                    }}
                  />
                )}
              />

              <Label className="optional">Description (optional)</Label>

              <Textarea
                placeholder="Description"
                {...register('description')}
              />

              <Line>
                <Confirm
                  type="submit"
                  disabled={!isValid || mutationCreateTag.isLoading}
                >
                  Confirm
                </Confirm>
              </Line>
            </Form>
          </Container>
        </Overlay>,
        portalDiv,
      )
    : null;
};
